import { useSessionContext } from '@common/hooks';
import {
    useDownloadOrganismeArchiveKarousselMutation,
    useExportEtablissementsFichiersForOrganismeMutation, useGetAllEtablissementsFichiersForOrganismeQuery,
    useGetAllOrganismeArchivesKarousselQuery,
} from '@common/services';
import { ArchiveKarousselMesArchivesDto, ArchiveKarousselProjection } from '@common/services/archive-karoussel';
import { FileItem } from '@common/ui';
import { unique } from "@common/utils";
import React, { useMemo } from 'react';

export const Archives = () => {
  const { organismeId } = useSessionContext();

  const { data: karousselArchives = [] as ArchiveKarousselMesArchivesDto[] } = useGetAllOrganismeArchivesKarousselQuery({
    projection: ArchiveKarousselProjection.MES_ARCHIVES,
    id: organismeId,
  }, { skip: !organismeId });

    const { data: etablissementFichiers = [] } = useGetAllEtablissementsFichiersForOrganismeQuery(
        { etablissementId: organismeId },
        { skip: !organismeId },
    );

  const [downloadArchiveKaroussel, { isLoading: isDownloadingKaroussel }] = useDownloadOrganismeArchiveKarousselMutation();
  const [exportEtablissementsFichiers, { isLoading: isExporting }] = useExportEtablissementsFichiersForOrganismeMutation();

  const archivesParAnnee = useMemo<ArchivesByAnnee[]>(() => {
      const documentsKaroussel: DocumentArchive[] = karousselArchives.map((a) => ({
          key: `KAROUSSEL-${a.id}`,
          annee: a.annee,
          title: a.nomFichier,
          subType: `${a.type} Karoussel`,
          dateModification: a.dateModification ?? a.dateCreation,
          source: 'KAROUSSEL',
          ids: [a.id],
      }));

      const groupesEtablissement = etablissementFichiers.reduce<Record<string, {
          annee: number,
          type: string;
          ids: number[];
          dateModification: string;
      }>>((acc, f) => {
          const annee = new Date(f.dateCreation).getFullYear();
          const key = `${annee}-${f.type}`;
          const dateModification = f.dateModification ?? f.dateCreation;

          acc[key] ??= { annee, type: f.type, ids: [], dateModification };
          acc[key].ids.push(f.etablissementId);
          if (dateModification > acc[key].dateModification) {
              acc[key].dateModification = dateModification;
          }
          return acc;
      }, {});

      const documentsEtablissement: DocumentArchive[] = Object.values(groupesEtablissement).map((g) => ({
          key: `ETABLISSEMENT-${g.annee}-${g.type}`,
          annee: g.annee,
          title: g.type,
          subType: g.type,
          type: g.type,
          dateModification: g.dateModification,
          source: 'ETABLISSEMENT',
          ids: unique(g.ids),
      }));

      const tousLesDocuments = [...documentsEtablissement, ...documentsKaroussel];

      const grouped = tousLesDocuments.reduce<Record<number, ArchivesByAnnee>>((acc, document) => {
          acc[document.annee] ??= { annee: document.annee, documents: [] };
          acc[document.annee].documents.push(document);
          return acc;
      }, {});

      return Object.values(grouped).sort((a, b) => b.annee - a.annee);
  }, [karousselArchives, etablissementFichiers]);

  const isDownloading = isDownloadingKaroussel || isExporting;

  const handleDownload = (document: DocumentArchive) => {
        if (document.source === 'KAROUSSEL') {
            // Il y a tout le temps un seul document Karoussel d'où ids[0]
            return downloadArchiveKaroussel({ id: organismeId, archiveId: document.ids[0] });
        }
        return exportEtablissementsFichiers({ anneeCampagne: document.annee, ids: document.ids, types: [document.type] });
    };

  return (
    <div className="fr-card flex flex-col gap-6" style={{ padding: '2em' }}>

      <h6 style={{ marginBottom: '8px' }}>Déclarations, Attestations et Alertes</h6>

      {archivesParAnnee.map((item, index) => (
        <section className="fr-accordion before:!shadow-none border-r border-l border-b border-gray-200 rounded-lg"
                 key={index}>
          <h3 className="fr-accordion__title">
            <button type="button" className="fr-accordion__btn" aria-expanded="false"
                    aria-controls={`accordion-${index}`}>
              {item.annee}
            </button>
          </h3>
          <div id={`accordion-${index}`} className="fr-collapse flex flex-col gap-6">
            {item.documents.map((document) => (
              <div key={document.key}>
                <FileItem title={document.title}
                          subType={document.subType}
                          downloadOptions={{
                            disabled: isDownloading,
                            fn: () => handleDownload(document),
                          }}
                          dateModification={document.dateModification}
                          fileBackgroundColor={'#007962'} />
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

interface DocumentArchive {
    key: string;
    annee: number;
    title: string;
    subType: string;
    type?: string;
    dateModification: string;
    source: 'KAROUSSEL' | 'ETABLISSEMENT';
    ids: number[];
}

interface ArchivesByAnnee {
    annee: number;
    documents: DocumentArchive[];
}